import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";

const CountUp = ({ value, prefix = "", suffix = "", start }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const duration = 1800;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, value]);
  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
};

export const ImpactStats = () => {
  const { t } = useLang();
  const items = t("stats.items");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="impact-stats" className="relative bg-[#F8F9FA] py-20 md:py-28 overflow-hidden">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Overline>{t("stats.overline")}</Overline>
            <h2 className="mt-5 font-display text-3xl md:text-5xl tracking-tight text-[#0A1128]">{t("stats.title")}</h2>
          </div>
        </Reveal>

        <div ref={ref} className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#0A1128]/10 border border-[#0A1128]/10">
          {items.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white px-6 py-10 md:py-14 text-center"
            >
              <div data-testid={`stat-value-${i}`} className="font-display text-4xl md:text-6xl text-[#B8901F] tracking-tight">
                <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} start={inView} />
              </div>
              <p className="mt-3 text-sm md:text-base text-[#4B5563] leading-snug">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[#4B5563]">{t("stats.note")}</p>
      </div>
    </section>
  );
};
